'use client';

import { useUpdateSharedState } from '@/hooks/state/shared-state';
import { clientErrorLoggerApiService } from '@/services/(frontend)/client-error-logger-api.service';
import { isMobileUtils } from '@/utils/mobile';
import { signOut } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { useClearCache } from './shared/clear-cache';

const WEBVIEW_CLOSE_DETECTION_DELAY = 2000;

export type MobileAuthenticationStatusType = 'loading' | 'authenticating' | 'authenticated' | 'unauthenticated' | 'signing-out';

type MobileAuthenticationStatus = {
  needsAuthentication: boolean;
  status: MobileAuthenticationStatusType;
};

type UseIsMobileType = {
  cleanupWebViewCloseDetection: () => void;
  getRoute: () => string;
  initializeWebViewCloseDetection: () => void;
  isMobile: boolean;
  mobileAuthStatus: MobileAuthenticationStatus;
  setMobileAuthStatus: (status: MobileAuthenticationStatus) => void;
};

export const useIsMobile = (): UseIsMobileType => {
  const pathname = usePathname();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();
  const [mobileAuthStatus, setMobileAuthStatus] = useState<MobileAuthenticationStatus>({ needsAuthentication: true, status: 'loading' });
  const [webViewCloseTimer, setWebViewCloseTimer] = useState<NodeJS.Timeout | null>(null);
  const clearCache = useClearCache();
  const userAgent = typeof window !== 'undefined' ? navigator.userAgent : '';
  const isMobile = isMobileUtils.checkIsMobileByPathname(pathname) || isMobileUtils.checkIsMobileByUserAgent(userAgent);

  const getRoute = (): string => {
    return isMobileUtils.getRoute(pathname, isMobile);
  };

  const initializeWebViewCloseDetection = (): void => {
    if (!isMobile) {
      return;
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
  };

  const cleanupWebViewCloseDetection = (): void => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    window.removeEventListener('pagehide', handlePageHide);

    if (webViewCloseTimer) {
      clearTimeout(webViewCloseTimer);
      setWebViewCloseTimer(null);
    }
  };

  const handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      // start a timer - if the page stays hidden for more than 2 seconds, assume the WebView was closed
      // TODO: Decide whether to always sign out immediately on visibility hidden, instead of waiting for the timer. (PBI-129911)
      setWebViewCloseTimer(setTimeout(() => {
        signOutFromMobileWebView();
      }, WEBVIEW_CLOSE_DETECTION_DELAY));

    } else if (document.visibilityState === 'visible' && webViewCloseTimer) {
      // cancel the timer if page becomes visible again
      clearTimeout(webViewCloseTimer);
      setWebViewCloseTimer(null);
    }
  };

  /**
   * Handles beforeunload event (WebView closing)
   */
  const handleBeforeUnload = (): void => {
    // use sendBeacon for reliable logout even if page is closing
    const data = JSON.stringify({ source: 'webview-close' });
    const blob = new Blob([data], { type: 'application/json' });
    const success = navigator.sendBeacon('/papi/auth/signout', blob);
    if (!success) {
      void clientErrorLoggerApiService.logError(new Error('Failed to send logout beacon on WebView close'));
    }
  };

  /**
   * Handles pagehide event (iOS WebView closing)
   */
  const handlePageHide = (event: PageTransitionEvent): void => {
    if (!event.persisted) {
      signOutFromMobileWebView();
    }
  };

  const signOutFromMobileWebView = async (): Promise<void> => {
    try {
      clearCache();
      setMobileAuthStatus({ needsAuthentication: true, status: 'signing-out' });
      await updateSharedState({ isAuthenticated: false });
      await signOut({ redirect: false });
    } catch (error) {
      void clientErrorLoggerApiService.logError(error);
    }
  };

  return { cleanupWebViewCloseDetection, getRoute, initializeWebViewCloseDetection, isMobile, mobileAuthStatus, setMobileAuthStatus };
};
